import { IStyle } from '@fluentui/react/lib/Styling';
import { ITextFieldProps, ITextFieldStyleProps, ITextFieldStyles, TextField } from '@fluentui/react/lib/TextField';

export interface IPTextFieldProps extends ITextFieldProps {
  hideChrome?: boolean;
}

type TextFieldStyleFunction = Extract<
  NonNullable<ITextFieldProps['styles']>,
  (styleProps: ITextFieldStyleProps) => unknown
>;
type TextFieldStyleObject = ReturnType<TextFieldStyleFunction>;

const hideChromeStyles: Partial<ITextFieldStyles> = {
  root: {
    selectors: {
      '& .ms-TextField-fieldGroup': {
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
      },
      '& .ms-TextField-fieldGroup.is-disabled': {
        background: 'transparent',
      },
      '& .ms-TextField-fieldGroup:after': {
        border: 'none',
      },
      '& .ms-TextField-field:focus': {
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
      },
      '& .ms-TextField-field:disabled': {
        background: 'transparent',
      },
    },
  },
  fieldGroup: {
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    selectors: {
      '&.is-disabled': {
        background: 'transparent',
      },
      '&.is-active': {
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
      },
      ':focus': {
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
      },
      ':focus-within': {
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
      },
      ':before': {
        border: 'none',
      },
      ':after': {
        border: 'none',
        boxShadow: 'none',
      },
      ':hover:before': {
        border: 'none',
      },
    },
  },
  field: {
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    selectors: {
      ':focus': {
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
      },
      ':disabled': {
        background: 'transparent',
      },
    },
  },
};

const mergeTextFieldStyles = (
  baseStyles: Partial<ITextFieldStyles>,
  styles: ITextFieldProps['styles'],
): ITextFieldProps['styles'] => {
  const asStyleObject = (style: unknown): Record<string, unknown> =>
    style && typeof style === 'object' && !Array.isArray(style) ? (style as Record<string, unknown>) : {};

  const getSelectors = (style: unknown): Record<string, IStyle> => {
    const styleObject = asStyleObject(style);

    return asStyleObject(styleObject.selectors) as Record<string, IStyle>;
  };

  const mergeSlot = (baseSlot: unknown, customSlot: unknown): IStyle =>
    ({
      ...asStyleObject(baseSlot),
      ...asStyleObject(customSlot),
      selectors: {
        ...getSelectors(baseSlot),
        ...getSelectors(customSlot),
      },
    }) as IStyle;

  const mergeStyles = (customStyles: TextFieldStyleObject): TextFieldStyleObject => ({
    ...baseStyles,
    ...customStyles,
    root: mergeSlot(baseStyles.root, customStyles.root),
    fieldGroup: mergeSlot(baseStyles.fieldGroup, customStyles.fieldGroup),
    field: mergeSlot(baseStyles.field, customStyles.field),
  });

  if (!styles) return baseStyles;

  if (typeof styles === 'function') {
    return (styleProps: ITextFieldStyleProps) => mergeStyles(styles(styleProps));
  }

  return mergeStyles(styles);
};

export const IPTextField = (props: IPTextFieldProps) => {
  const { hideChrome, styles, ...textFieldProps } = props;

  return (
    <TextField {...textFieldProps} styles={hideChrome ? mergeTextFieldStyles(hideChromeStyles, styles) : styles} />
  );
};
